<template>
  <div class="page-layout">
    <div class="page-content">
      <div v-if="loading" class="panel">
        <p class="empty-state">Загрузка...</p>
      </div>

      <div v-else-if="error" class="panel">
        <p class="error-message">{{ error }}</p>
      </div>

      <div v-else-if="profile" class="panel public-profile">
        <div class="public-profile-header">
          <UserAvatar :user="profile" size="lg" />
          <div>
            <h1>{{ userDisplayName(profile) }}</h1>
            <p v-if="profile.username" class="public-profile-username">@{{ profile.username }}</p>
          </div>
        </div>

        <p v-if="isPlainAbout" class="public-profile-about">{{ profile.about }}</p>
        <div
          v-else-if="sanitizedAbout"
          class="public-profile-about public-profile-about--rich"
          v-html="sanitizedAbout"
        />

        <div class="public-profile-links">
          <router-link
            class="btn btn-secondary"
            :to="{ name: 'public-profile-tracks', params: { username: profile.username } }"
          >
            Публичные треки ({{ profile.public_tracks_count }})
          </router-link>
          <router-link
            class="btn btn-secondary"
            :to="{ name: 'public-profile-trips', params: { username: profile.username } }"
          >
            Публичные поездки ({{ profile.public_trips_count }})
          </router-link>
          <router-link
            class="btn btn-secondary"
            :to="{ name: 'public-profile-events', params: { username: profile.username } }"
          >
            Публичные события ({{ profile.public_events_count ?? 0 }})
          </router-link>
        </div>

        <section v-if="profile.vehicles?.length" class="public-profile-vehicles">
          <h2>Автомобили</h2>
          <ul class="vehicles-list">
            <li v-for="vehicle in profile.vehicles" :key="vehicle.id">
              <VehicleCard :vehicle="vehicle" />
            </li>
          </ul>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import DOMPurify from 'dompurify'
import UserAvatar from '../components/UserAvatar.vue'
import VehicleCard from '../components/VehicleCard.vue'
import { fetchPublicProfile } from '../api/publicProfiles'
import { userDisplayName } from '../utils/userDisplay'
import { redirectToLoginIfUnauthorized } from '../utils/profileAccess'
import {
  EVENT_DESCRIPTION_ALLOWED_ATTR,
  EVENT_DESCRIPTION_ALLOWED_TAGS,
  isHtmlContent,
} from '../utils/richDescription'

const route = useRoute()
const router = useRouter()

const profile = ref(null)
const loading = ref(true)
const error = ref('')

const isPlainAbout = computed(() => {
  return profile.value?.about && !isHtmlContent(profile.value.about)
})

const sanitizedAbout = computed(() => {
  if (!profile.value?.about || isPlainAbout.value) return ''

  return DOMPurify.sanitize(profile.value.about, {
    ALLOWED_TAGS: EVENT_DESCRIPTION_ALLOWED_TAGS,
    ALLOWED_ATTR: EVENT_DESCRIPTION_ALLOWED_ATTR,
  })
})

async function loadProfile() {
  loading.value = true
  error.value = ''
  profile.value = null
  let redirecting = false

  try {
    const { data } = await fetchPublicProfile(route.params.username)
    profile.value = data.user
  } catch (err) {
    if (redirectToLoginIfUnauthorized(err, router, route)) {
      redirecting = true
      return
    }

    if (err.response?.status === 404) {
      error.value = 'Профиль не найден или скрыт'
    } else {
      error.value = 'Не удалось загрузить профиль'
    }
  } finally {
    if (!redirecting) {
      loading.value = false
    }
  }
}

onMounted(loadProfile)
watch(() => route.params.username, loadProfile)
</script>

<style scoped>
.public-profile-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.public-profile-header h1 {
  margin: 0 0 0.25rem;
}

.public-profile-username {
  margin: 0;
  color: #64748b;
}

.public-profile-about {
  margin: 0 0 1.5rem;
  color: #334155;
  line-height: 1.6;
}

.public-profile-about--rich :deep(h2),
.public-profile-about--rich :deep(h3) {
  margin: 1rem 0 0.5rem;
  font-size: 1rem;
}

.public-profile-about--rich :deep(ul),
.public-profile-about--rich :deep(ol) {
  margin: 0.5rem 0;
  padding-left: 1.25rem;
}

.public-profile-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.public-profile-vehicles {
  margin-top: 1.5rem;
}

.public-profile-vehicles h2 {
  margin: 0 0 1rem;
  font-size: 1.125rem;
}

.vehicles-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
</style>
